import { toast } from "sonner";
import QRCode from "qrcode";
import { fmtDay } from "@/lib/utils";

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const fmtAt = (at) => new Date(at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });

export const requestCode = (id) => id.slice(0, 8).toUpperCase();
export const issueUrl = (id) => `${window.location.origin}/store?issue=${id}`;

export const requestEntry = (r) => ({
  qr: r.id,
  heading: `${(r.requester_location || "—").trim()} · ${r.requested_by_name}`,
  sub: `${(r.requester_role || "").replace(/^./, (c) => c.toUpperCase())} request to ${r.store} Store · ${fmtAt(r.created_at)}${r.required_by ? ` · Required by ${fmtDay(r.required_by)}` : ""}`,
  status: r.status,
  items: r.items.map((it) => ({ name: it.name, unit: it.unit, qty: it.qty, issued: r.status === "Pending" ? "" : it.issued_qty })),
  remarks: r.remarks,
});

export const transferEntry = (t) => ({
  heading: `${t.from_store} → ${t.to_store}`,
  sub: `Transfer${t.by_name ? ` by ${t.by_name}` : ""} · ${fmtAt(t.created_at)}`,
  status: "Transferred",
  items: t.items.map((it) => ({ name: it.name, unit: it.unit, qty: it.qty, issued: "" })),
  remarks: t.remarks,
});

const consolidated = (entries) => {
  const map = {};
  for (const e of entries) for (const it of e.items) {
    const k = `${it.name}__${it.unit}`;
    map[k] = map[k] || { name: it.name, unit: it.unit, qty: 0 };
    map[k].qty += Number(it.qty) || 0;
  }
  return Object.values(map).sort((a, b) => a.name.localeCompare(b.name));
};

export const printMaterialList = async ({ title, subtitle, entries, issuedLabel = "Issued" }) => {
  if (!entries.length) return toast.error("Nothing to print");
  const w = window.open("", "_blank");
  if (!w) return toast.error("Allow pop-ups to print");
  const qrs = await Promise.all(entries.map((e) => (e.qr ? QRCode.toDataURL(issueUrl(e.qr), { margin: 1, width: 120 }) : null)));
  const totals = consolidated(entries);
  const totalRows = totals.map((t) => `<tr><td class="box"></td><td>${esc(t.name)}</td><td class="num">${+t.qty.toFixed(3)}</td><td>${esc(t.unit)}</td></tr>`).join("");
  const blocks = entries.map((e, idx) => `
    <div class="card">
      <div class="head"><div class="grow">
      <div class="card-h"><b>${esc(e.heading)}</b><span class="st">${esc(e.status)}</span></div>
      <div class="sub">${esc(e.sub)}</div></div>
      ${qrs[idx] ? `<div class="qr"><img src="${qrs[idx]}" alt="QR"/><div>Scan to issue<br/><b>#${esc(requestCode(e.qr))}</b></div></div>` : ""}</div>
      <table><thead><tr><th class="box">✓</th><th>Material</th><th class="num">Qty</th><th>Unit</th><th class="num">${esc(issuedLabel)}</th></tr></thead>
      <tbody>${e.items.map((it) => `<tr><td class="box"></td><td>${esc(it.name)}</td><td class="num">${it.qty}</td><td>${esc(it.unit)}</td><td class="num fill">${it.issued === "" || it.issued == null ? "" : esc(it.issued)}</td></tr>`).join("")}</tbody></table>
      ${e.remarks ? `<p class="rem">Note: ${esc(e.remarks)}</p>` : ""}
    </div>`).join("");
  w.document.write(`<!doctype html><html><head><title>${esc(title)}</title><style>
    body{font-family:Arial,sans-serif;color:#111;margin:20px;font-size:12px}
    header{display:flex;align-items:center;justify-content:space-between;border-bottom:3px solid #ea580c;padding-bottom:8px;margin-bottom:14px}
    header img{height:40px} h1{font-size:20px;margin:0} h2{font-size:15px;margin:18px 0 6px;border-bottom:1px solid #ccc;padding-bottom:3px}
    table{width:100%;border-collapse:collapse} td,th{border:1px solid #bbb;padding:5px 7px;text-align:left} th{background:#fde7d9}
    .num{text-align:right;font-weight:bold} .box{width:16px} .fill{min-width:60px}
    .card{border:1px solid #999;border-radius:6px;padding:8px;margin-bottom:10px;break-inside:avoid}
    .card-h{display:flex;justify-content:space-between;font-size:14px} .st{font-size:11px;border:1px solid #333;border-radius:4px;padding:0 6px}
    .sub{color:#555;margin:2px 0 6px}
    .head{display:flex;gap:10px;align-items:flex-start}.grow{flex:1}
    .qr{text-align:center;font-size:10px;color:#333;line-height:1.3}.qr img{width:84px;height:84px;display:block} .rem{font-style:italic;margin:6px 0 0}
    .sign{margin-top:26px;display:flex;gap:40px} .sign div{flex:1;border-top:1px solid #333;padding-top:4px;color:#555}
    @media print{body{margin:10mm}}
  </style></head><body>
    <header><img src="${window.location.origin}/baron-logo.jpg" alt="Baron Kitchen"/><div style="text-align:right"><h1>${esc(title)}</h1>
    <div>${esc(subtitle)} · ${entries.length} record(s) · Printed ${esc(fmtAt(new Date()))}</div></div></header>
    ${entries.length > 1 ? `<h2>Consolidated Pick List</h2><table><thead><tr><th class="box">✓</th><th>Material</th><th class="num">Total Qty</th><th>Unit</th></tr></thead><tbody>${totalRows}</tbody></table><h2>Details</h2>` : ""}
    ${blocks}
    <div class="sign"><div>Issued / Sent by</div><div>Received by</div><div>Date &amp; Time</div></div>
    <script>window.onload=function(){setTimeout(function(){window.print()},300)}</script>
  </body></html>`);
  w.document.close();
};
